import { MutableRefObject, RefObject, useState } from 'react';
import { useBreadcrumbTruncationContext } from './BreadcrumbTruncationContext';
import { getBreadcrumbContentWidths, getRefWidth } from './breadcrumbsHelper';

export enum BreadcrumbPlacement {
  ContextBar,
  BelowHeader,
}

export const calculateTruncationIndex = ({
  breadcrumbRefs,
  containerRef,
  truncationIconRef,
  emptyContextBarRef,
  pageTitleRef,
  caretIconRef,
}: {
  breadcrumbRefs: MutableRefObject<HTMLElement[] | null>;
  containerRef: RefObject<HTMLDivElement>;
  truncationIconRef: MutableRefObject<HTMLDivElement | null>;
  emptyContextBarRef: MutableRefObject<HTMLDivElement | null>;
  pageTitleRef: MutableRefObject<HTMLAnchorElement | null>;
  caretIconRef: RefObject<HTMLElement | null>;
}) => {
  if (!breadcrumbRefs.current) return -1;

  const breadcrumbsTotalWidths = getBreadcrumbContentWidths(
    breadcrumbRefs.current,
  );
  const truncationIconWidth = getRefWidth(truncationIconRef);
  const outerWidth = getRefWidth(containerRef);
  const outerHeight = containerRef?.current?.offsetHeight ?? -1;
  const emptyContextBarWidth = getRefWidth(emptyContextBarRef);
  const caretIconWidth = getRefWidth(caretIconRef);
  const pageTitleWidth = getRefWidth(pageTitleRef);

  if (
    emptyContextBarWidth >
    pageTitleWidth + truncationIconWidth + caretIconWidth
  )
    return breadcrumbRefs.current.length - 1;

  const outerWidthNoCaret =
    outerHeight <= 0 ? outerWidth - caretIconWidth : outerWidth;
  const untruncateIndex = breadcrumbsTotalWidths.findIndex(
    (width) => width > outerWidthNoCaret,
  );

  return untruncateIndex > -1
    ? breadcrumbRefs.current.length - 1 - untruncateIndex
    : -1;
};

export const calculateContextBarPlacement = ({
  containerRef,
  truncationIconRef,
  emptyContextBarRef,
  pageTitleRef,
  caretIconRef,
}: {
  containerRef: RefObject<HTMLDivElement>;
  truncationIconRef: MutableRefObject<HTMLDivElement | null>;
  emptyContextBarRef: MutableRefObject<HTMLDivElement | null>;
  pageTitleRef: MutableRefObject<HTMLAnchorElement | null>;
  caretIconRef: RefObject<HTMLElement | null>;
}) => {
  const truncationIconWidth = getRefWidth(truncationIconRef);
  const outerWidth = getRefWidth(containerRef);
  const emptyContextBarWidth = getRefWidth(emptyContextBarRef);
  const caretIconWidth = getRefWidth(caretIconRef);
  const pageTitleWidth = getRefWidth(pageTitleRef);
  const totalContentWidth =
    pageTitleWidth + truncationIconWidth + caretIconWidth;

  if (outerWidth <= 0) {
    return BreadcrumbPlacement.BelowHeader;
  }

  if (emptyContextBarWidth > totalContentWidth) {
    return BreadcrumbPlacement.ContextBar;
  }
};

export const useBreadcrumbTruncation = ({
  containerRef,
}: {
  containerRef: RefObject<HTMLDivElement>;
}) => {
  const [truncationIndex, setTruncationIndex] = useState<number>(-1);
  const {
    emptyContextBarRef,
    truncationIconRef,
    caretIconRef,
    pageTitleRef,
    setTruncatedOverflow,
    truncatedOverflow,
    breadcrumbRefs,
  } = useBreadcrumbTruncationContext();

  const handleTruncation = () => {
    // get placement on where to set the contextbar
    const placement = calculateContextBarPlacement({
      containerRef,
      truncationIconRef,
      emptyContextBarRef,
      pageTitleRef,
      caretIconRef,
    });

    // move breadcrumbs to display under the header
    if (!truncatedOverflow && placement === BreadcrumbPlacement.BelowHeader) {
      setTruncatedOverflow(true);
      setTruncationIndex(-1);
    }

    // when breadcrumbs are currently rendering below the header and the new placement
    // can fit into the context bar
    if (truncatedOverflow && placement === BreadcrumbPlacement.ContextBar) {
      setTruncatedOverflow(false);
    }

    const newTruncationIndex = calculateTruncationIndex({
      breadcrumbRefs,
      containerRef,
      truncationIconRef,
      emptyContextBarRef,
      pageTitleRef,
      caretIconRef,
    });
    setTruncationIndex(newTruncationIndex);
  };

  return {
    handleTruncation,
    truncationIndex,
  };
};
